import { useCallback, useMemo, useState } from 'react';
import { Box, Checkbox, Link, Stack, Typography, useTheme } from '@mui/material';
import {
  DataGridPremium,
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
  GridColDef,
} from '@mui/x-data-grid-premium';
import { DraftCondition, GroupedConditions } from '@retail/calculus/types';
import { useAppTFunction } from '@retail/app/i18n';
import { dataGridNoHorizontalPaddingClass } from '@shared/styles';
import {
  CustomDetailPanelToggle,
  DraftConditionCountChips,
  DraftTableContent,
} from '@retail/calculus/components';
import { useIsFeatureEnabled, useSelectedOrgUnit } from '@retail/app/stores/selected-context';
import { OrgUnitType } from '@retail/mgpris/config-types';
import { useNavigate } from 'react-router';
import { mgPrisRoutes } from '@retail/mgpris/config';
import { OrgUnitAssortment } from '@retail/mgpris/config-types';

interface Props {
  draftConditions: DraftCondition[];
  groupedConditions: GroupedConditions[];
  selectedConditionIds: number[];
  priceLists: OrgUnitAssortment[];
  isLoading: boolean;
  language: string;
  handleDeleteCondition: (conditionId: number) => void;
  handleSelectConditionId: (conditionId: number) => void;
  handleSelectAllConditionIdsForSupplierOrAssortment: (assortmentOrSupplierId: number) => void;
}

type GridRowCondition = GridColDef<GroupedConditions>;

const baseColumnProps: Partial<GridRowCondition> = {
  width: 150,
  sortable: false,
  filterable: false,
  hideable: false,
  disableColumnMenu: true,
  disableReorder: true,
  resizable: false,
  headerAlign: 'left',
  align: 'left',
};

export const DraftAffectedItemsTable = ({
  draftConditions,
  groupedConditions,
  selectedConditionIds,
  priceLists,
  isLoading,
  language,
  handleDeleteCondition,
  handleSelectConditionId,
  handleSelectAllConditionIdsForSupplierOrAssortment,
}: Props) => {
  const t = useAppTFunction();
  const { orgUnitType } = useSelectedOrgUnit();
  const theme = useTheme();
  const palette = theme.vars.palette;
  const navigate = useNavigate();
  const salesPriceConditionsIsEnabled = useIsFeatureEnabled('sales-price-condition');

  const columns: GridRowCondition[] = useMemo(
    () => [
      {
        ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
        field: 'checkbox',
        align: 'center',
        renderCell: ({ row }) => {
          const conditionIds = row.conditions
            .map((condition) => condition.condition.types.map((type) => type.id))
            .flat();

          const conditionLength = conditionIds.filter((condition) =>
            selectedConditionIds.includes(condition)
          ).length;

          const allConditionsChecked =
            row.conditions?.map((cond) => cond.condition.types).flat().length === conditionLength;

          return (
            <Checkbox
              checked={allConditionsChecked}
              indeterminate={conditionLength > 0 && !allConditionsChecked}
              onClick={(e) => {
                e.stopPropagation();
                handleSelectAllConditionIdsForSupplierOrAssortment(row.assortmentOrSupplierId);
              }}
            />
          );
        },
      },
      {
        ...baseColumnProps,
        field: 'supplier',
        width: 240,
        headerName: t('markup.supplier'),
        renderCell: ({ rowNode, field, row }) => {
          if (rowNode.type === 'group' && field === rowNode.groupingField) {
            return '';
          }
          return (
            <Typography variant="subtitle2">
              {row.assortmentOrSupplierName ?? row.assortmentOrSupplierId}
            </Typography>
          );
        },
      },
      {
        ...baseColumnProps,
        field: 'conditionCount',
        width: 190,
        headerName: t('markup.supplier'),
        renderCell: ({ row }) => (
          <DraftConditionCountChips
            draftConditions={groupedConditions.find(
              (conditions) => conditions.assortmentOrSupplierId === row.assortmentOrSupplierId
            )}
          />
        ),
      },
      {
        ...baseColumnProps,
        field: 'priceChanges',
        flex: 1,
        headerName: t('markup.supplier'),
        align: 'right',
        renderCell: ({ row }) => {
          if (orgUnitType === OrgUnitType.RETAIL_LOCATION_LIGHT) return null;

          return (
            <Box display="flex" gap={0.5}>
              <Link
                component="button"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(
                    mgPrisRoutes.approval.calculus.getFullLinkPath() +
                      `?supplierId=${row.assortmentOrSupplierId}`
                  );
                }}
              >
                {t('calculus.prices.seePriceChanges')}
              </Link>
            </Box>
          );
        },
      },
      {
        ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
        align: 'right',
        renderCell: (params) => <CustomDetailPanelToggle id={params.id} value={params.value} />,
      },
    ],
    [
      t,
      selectedConditionIds,
      handleSelectAllConditionIdsForSupplierOrAssortment,
      groupedConditions,
      orgUnitType,
      navigate,
    ]
  );

  const getDetailPanelContent = useCallback(
    ({ conditions }: GroupedConditions) => {
      const onSelectConditionId = (conditionId: number) => handleSelectConditionId(conditionId);
      return (
        <DraftTableContent
          conditions={conditions}
          priceLists={priceLists}
          isLoading={isLoading}
          language={language}
          checkedConditionIdsForSupplier={selectedConditionIds}
          salesPriceConditionsIsEnabled={salesPriceConditionsIsEnabled}
          handleSelectConditionId={onSelectConditionId}
          handleDeleteCondition={handleDeleteCondition}
        />
      );
    },
    [
      priceLists,
      isLoading,
      language,
      selectedConditionIds,
      salesPriceConditionsIsEnabled,
      handleDeleteCondition,
      handleSelectConditionId,
    ]
  );

  const [detailPanelsOpen, setDetailPanelsOpen] = useState<Set<number>>(
    draftConditions.length === 1
      ? new Set([groupedConditions[0].assortmentOrSupplierId])
      : new Set()
  );

  const onRowClick = useCallback(
    (id: number) =>
      detailPanelsOpen.has(id)
        ? setDetailPanelsOpen(new Set([...detailPanelsOpen].filter((panelId) => panelId !== id)))
        : setDetailPanelsOpen(new Set([...detailPanelsOpen, id])),
    [detailPanelsOpen]
  );

  return (
    <DataGridPremium<GroupedConditions>
      className={dataGridNoHorizontalPaddingClass}
      detailPanelExpandedRowIds={detailPanelsOpen}
      onDetailPanelExpandedRowIdsChange={(ids) => setDetailPanelsOpen(ids as Set<number>)}
      onRowClick={({ id }) => onRowClick(id as number)}
      getRowId={(row) => row.assortmentOrSupplierId}
      columns={columns}
      rows={groupedConditions}
      rowHeight={58}
      columnHeaderHeight={0}
      getDetailPanelContent={({ row }) => getDetailPanelContent(row)}
      getDetailPanelHeight={() => 'auto'}
      hideFooter
      rowSelection
      slots={{
        noRowsOverlay: () => (
          <Stack alignItems="center" justifyContent="center" gap={2} height="100%">
            <Typography variant="h3" color="text.secondary">
              {t('calculus.messages.noUpdates')}
            </Typography>
          </Stack>
        ),
      }}
      sx={{
        '.MuiDataGrid-row': {
          bgcolor: `${palette.grey[50]} !important`,
          borderBottom: `1px solid ${palette.divider}`,
          ':hover': { cursor: 'pointer', bgcolor: `${palette.primary.A100} !important` },
        },
      }}
    />
  );
};
